import React, { useMemo, useState } from 'react';
import data from '../data/roadmap.json';
import blurpleLogo from '../assets/blurple.png';
import './RoadMapPage.css';
import { FiCheckCircle, FiClock, FiTarget } from 'react-icons/fi';
import { IoArrowBack } from 'react-icons/io5';

type StatusType = 'done' | 'in progress' | 'todo';

type RoadmapItem = {
  month: string;
  status: StatusType;
  title: string;
  description: string;
};

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

function parseMonthCode(code: string) {
  const mon = code.slice(0, 3).toUpperCase();
  const year = Number(code.slice(3));
  const monthIndex = MONTHS.indexOf(mon);
  const quarterIndex = Math.floor(monthIndex / 3);
  return { monthIndex, year, quarterIndex };
}

function formatMonth(code: string) {
  const { monthIndex, year } = parseMonthCode(code);
  const date = new Date(year, monthIndex, 1);
  return date.toLocaleString(undefined, { month: 'short', year: 'numeric' });
}

const StatusIcon: React.FC<{ status: StatusType }> = ({ status }) => {
  if (status === 'done') return React.createElement(FiCheckCircle as React.ComponentType<any>, { color: '#19a974', size: 18 });
  if (status === 'in progress') return React.createElement(FiClock as React.ComponentType<any>, { color: '#f5a623', size: 18 });
  return React.createElement(FiTarget as React.ComponentType<any>, { color: '#6057dc', size: 18 });
};

interface RoadMapPageProps {
  onBack?: () => void;
}

const RoadMapPage: React.FC<RoadMapPageProps> = ({ onBack }) => {
  const items = data as RoadmapItem[];
  const [showTuner, setShowTuner] = useState(false);
  const [tweak, setTweak] = useState({
    lineWidth: 8,
    elbowSize: 32,
    gap: 21,
    rowGap: 17,
    cardMaxWidth: 255,
    betweenQuarters: 6,
    quarterPaddingY: 24,
    spineOffsetLeft: -13,
    spineOffsetRight: -13,
    elbowXLeft: 19,
    elbowXRight: 19,
    elbowY: 3,
    horizInset: 1,
    horizY: -16,
    dotXLeft: -8,
    dotXRight: 8,
    logoStartX: 47,
    logoStartY: -30,
    logoEndLeftX: 49,
    logoEndLeftY: 26,
    logoEndRightX: 0,
    logoEndRightY: 0,
  });

  const groups = useMemo(() => {
    const byQuarter: Array<{ key: string; align: 'left' | 'right'; milestones: RoadmapItem[] }> = [];
    if (items.length === 0) return byQuarter;

    const firstYear = parseMonthCode(items[0].month).year;
    const lastYear = parseMonthCode(items[items.length - 1].month).year;
    const quarters: Array<{ year: number; q: number }> = [];
    for (let y = firstYear; y <= lastYear; y += 1) {
      for (let q = 0; q < 4; q += 1) quarters.push({ year: y, q });
    }

    const alignForQuarter = (index: number): 'left' | 'right' => (index % 2 === 0 ? 'left' : 'right');

    let quarterIndex = 0;
    for (const { year, q } of quarters) {
      const key = `${year}-Q${q + 1}`;
      const align = alignForQuarter(quarterIndex);
      const milestones = items.filter((it) => {
        const p = parseMonthCode(it.month);
        return p.year === year && Math.floor(p.monthIndex / 3) === q;
      });
      // Always push, even if no milestones, so the weave alternates every quarter
      byQuarter.push({ key, align, milestones });
      quarterIndex += 1;
    }
    // Trim leading and trailing quarters without milestones so the roadmap
    // starts and ends where the first/last milestones are
    const firstWith = byQuarter.findIndex(g => g.milestones.length > 0);
    const lastWith = byQuarter.length - 1 - [...byQuarter].reverse().findIndex(g => g.milestones.length > 0);
    if (firstWith === -1) {
      return [];
    }
    return byQuarter.slice(firstWith, lastWith + 1);
  }, [items]);

  const styleVars: React.CSSProperties = {
    ['--line-width' as any]: `${tweak.lineWidth}px`,
    ['--elbow-size' as any]: `${tweak.elbowSize}px`,
    ['--gap' as any]: `${tweak.gap}px`,
    ['--row-gap' as any]: `${tweak.rowGap}px`,
    ['--card-max-width' as any]: `${tweak.cardMaxWidth}px`,
    ['--between-quarters' as any]: `${tweak.betweenQuarters}px`,
    ['--quarter-padding-y' as any]: `${tweak.quarterPaddingY}px`,
    ['--header-offset' as any]: `${-Math.max(0, tweak.quarterPaddingY)}px`,
    ['--spine-offset-left' as any]: `${tweak.spineOffsetLeft}px`,
    ['--spine-offset-right' as any]: `${tweak.spineOffsetRight}px`,
    ['--elbow-x-left' as any]: `${tweak.elbowXLeft}px`,
    ['--elbow-x-right' as any]: `${tweak.elbowXRight}px`,
    ['--elbow-y' as any]: `${tweak.elbowY}px`,
    ['--horiz-inset-adjust' as any]: `${tweak.horizInset}px`,
    ['--horiz-y' as any]: `${tweak.horizY}px`,
    ['--dot-x-left' as any]: `${tweak.dotXLeft}px`,
    ['--dot-x-right' as any]: `${tweak.dotXRight}px`,
    ['--logo-start-x' as any]: `${tweak.logoStartX}px`,
    ['--logo-start-y' as any]: `${tweak.logoStartY}px`,
    ['--logo-end-left-x' as any]: `${tweak.logoEndLeftX}px`,
    ['--logo-end-left-y' as any]: `${tweak.logoEndLeftY}px`,
    ['--logo-end-right-x' as any]: `${tweak.logoEndRightX}px`,
    ['--logo-end-right-y' as any]: `${tweak.logoEndRightY}px`,
  };

  return (
    <div className="page-content">
      <div className="roadmap-page" style={styleVars}>
        <div className="rm-header">
          <button className="rm-back-button" onClick={onBack} aria-label="Back">
            {React.createElement(IoArrowBack as React.ComponentType<any>)}
          </button>
          <div className="rm-title">Roadmap</div>
        </div>
        <br />
        <br />
        <div className="quarters">
          {groups.map((group, groupIdx) => (
            <section key={group.key} className={["quarter", group.align].join(' ')} aria-label={group.key}>
              {groupIdx > 0 && (
                <div className="connector connector-top">
                  <div className="horiz" />
                </div>
              )}
              {groupIdx === 0 && (
                <img src={blurpleLogo} alt="start" className={`line-marker line-start ${group.align === 'right' ? 'right' : 'left'}`} />
              )}
              <div className="quarter-grid">
                {group.milestones.length === 0 ? (
                  <div className="vertical-spine-fallback" />
                ) : group.milestones.map((m, i) => {
                  const isFirst = i === 0;
                  const isLast = i === group.milestones.length - 1;
                  return (
                    <React.Fragment key={`${groupIdx}-${i}`}>
                      <div className="dot" />
                      <article className={["card", isFirst ? 'is-first' : '', isLast ? 'is-last' : ''].join(' ').trim()}>
                        <div className="month">{formatMonth(m.month)}</div>
                        <div className="row">
                          <StatusIcon status={m.status} />
                          <h3 className="title">{m.title}</h3>
                        </div>
                        <div className="desc">{m.description}</div>
                      </article>
                    </React.Fragment>
                  );
                })}
              </div>
              {groupIdx === groups.length - 1 && (
                <img src={blurpleLogo} alt="end" className="line-marker line-end" />
              )}
              {/* Only one connector per boundary; top on all but first quarter */}
            </section>
          ))}
        </div>
      </div>
      {/* Logo Tuner (hidden by default) */}
      {false && (
      <div style={{ position: 'fixed', right: 8, top: 64, width: 260, zIndex: 9999, background: 'rgba(0,0,0,0.75)', color: '#fff', padding: 12, borderRadius: 10, backdropFilter: 'blur(6px)' }}>
        <strong style={{ display: 'block', marginBottom: 8 }}>Header & Logo Tuner</strong>
        {[
          ['quarterPaddingY', 0, 36, 'Header Offset'],
          ['logoStartX', -200, 200, 'Start X'],
          ['logoStartY', -200, 200, 'Start Y'],
          ['logoEndLeftX', -200, 200, 'End X (Left Quarter)'],
          ['logoEndLeftY', -200, 200, 'End Y (Left Quarter)'],
          ['logoEndRightX', -200, 200, 'End X (Right Quarter)'],
          ['logoEndRightY', -200, 200, 'End Y (Right Quarter)'],
        ].map(([key, min, max, label]) => (
          <div key={String(key)} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, margin: '6px 0' }}>
            <label style={{ fontSize: 12 }}>{String(label)}</label>
            <span style={{ fontSize: 12, opacity: 0.8 }}>{(tweak as any)[key]}px</span>
            <input
              type="range"
              min={Number(min)}
              max={Number(max)}
              value={(tweak as any)[key] as number}
              onChange={(e) => setTweak((prev) => ({ ...prev, [String(key)]: Number(e.target.value) }))}
            />
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default RoadMapPage;


